import React from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Box, Typography } from '@mui/material'
import { useAppSelector } from '../../hooks/redux'

export const EmployeeUtilizationChart: React.FC = () => {
  const { dashboardMetrics } = useAppSelector((state) => state.realtime)

  // Mock data for the last 7 days - in real app, this would come from the API
  const data = [
    {
      day: 'Mon',
      utilization: 85,
      efficiency: 78,
      capacity: 90,
    },
    {
      day: 'Tue',
      utilization: 88,
      efficiency: 82,
      capacity: 92,
    },
    {
      day: 'Wed',
      utilization: 92,
      efficiency: 85,
      capacity: 95,
    },
    {
      day: 'Thu',
      utilization: 87,
      efficiency: 80,
      capacity: 88,
    },
    {
      day: 'Fri',
      utilization: 90,
      efficiency: 88,
      capacity: 93,
    },
    {
      day: 'Sat',
      utilization: 75,
      efficiency: 70,
      capacity: 80,
    },
    {
      day: 'Sun',
      utilization: dashboardMetrics.employeeUtilization || 82,
      efficiency: dashboardMetrics.productionEfficiency || 75,
      capacity: 85,
    },
  ]

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            p: 2.5,
            border: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.08)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: entry.color }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                {entry.name}: {entry.value}%
              </Typography>
            </Box>
          ))}
        </Box>
      )
    }
    return null
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <defs>
            <linearGradient id="utilizationGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2196f3" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#2196f3" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="efficiencyGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#4caf50" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#4caf50" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="capacityGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ff9800" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#ff9800" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.06)" />
          <XAxis
            dataKey="day"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 13, fill: '#666', fontWeight: 600 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 13, fill: '#666', fontWeight: 600 }}
            domain={[0, 100]}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            height={40}
            iconType="line"
            wrapperStyle={{ fontSize: '14px', fontWeight: 600 }}
          />
          <Line
            type="monotone"
            dataKey="utilization"
            stroke="#2196f3"
            strokeWidth={3}
            dot={{ fill: '#2196f3', strokeWidth: 2, r: 5, filter: 'drop-shadow(0 2px 4px rgba(33, 150, 243, 0.3))' }}
            activeDot={{ r: 7, stroke: '#2196f3', strokeWidth: 3, fill: 'white', filter: 'drop-shadow(0 2px 8px rgba(33, 150, 243, 0.5))' }}
            name="Utilization"
            animationDuration={1000}
          />
          <Line
            type="monotone"
            dataKey="efficiency"
            stroke="#4caf50"
            strokeWidth={3}
            dot={{ fill: '#4caf50', strokeWidth: 2, r: 5, filter: 'drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))' }}
            activeDot={{ r: 7, stroke: '#4caf50', strokeWidth: 3, fill: 'white', filter: 'drop-shadow(0 2px 8px rgba(76, 175, 80, 0.5))' }}
            name="Efficiency"
            animationDuration={1000}
            animationBegin={200}
          />
          <Line
            type="monotone"
            dataKey="capacity"
            stroke="#ff9800"
            strokeWidth={3}
            dot={{ fill: '#ff9800', strokeWidth: 2, r: 5, filter: 'drop-shadow(0 2px 4px rgba(255, 152, 0, 0.3))' }}
            activeDot={{ r: 7, stroke: '#ff9800', strokeWidth: 3, fill: 'white', filter: 'drop-shadow(0 2px 8px rgba(255, 152, 0, 0.5))' }}
            name="Capacity"
            animationDuration={1000}
            animationBegin={400}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}
